import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { ModalGeneric } from '../../../../ui/generics';
import { ArticleLabelTypes, ArticleLabelTypesDisplayNames, isInstantArticleLabelType } from '../../core/enums/ArticleLabelTypes';
import { ArticleLabelsService } from '../../core/services/ArticleLabelsService';
import { IArticle } from '../../../../core/interfaces';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { NotificationService } from '../../../../core/services';
import { ArticleLabelsErrorResolverService } from '../../core/services/ArticleLabelsErrorResolverService';
import { AddEditRoundLabelModalComponent } from '../add-edit-round-label-modal/add-edit-round-label-modal.component';
import { LabelTypesEnum } from '../../../labels-printer/core/enums';

@Component({
    selector: 'app-add-article-label-modal',
    templateUrl: './add-article-label-modal.component.html',
    styleUrl: './add-article-label-modal.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AddArticleLabelModalComponent extends ModalGeneric {

  @ViewChild('addEditRoundLabelModalComponent') addEditRoundLabelModalComponent: AddEditRoundLabelModalComponent;

  @Output() refresh  = new EventEmitter<void>();
  @Output() openOwnBrand = new EventEmitter<{ variantId: number; labelType: string }>();

  public isLoading = false;
  public selectedArticleLabelType: ArticleLabelTypes | string;
  public selectedArticleVariantIds: number[] = [];
  public article: IArticle;
  public readonly articleLabelTypes = ArticleLabelTypes;
  public readonly articleLabelTypesDisplayNames = ArticleLabelTypesDisplayNames;
  public readonly ownBrandSmallRoundType = 'SmallRound';

  public constructor(
    protected readonly cdr: ChangeDetectorRef,
    private readonly _articleLabelsService: ArticleLabelsService,
    private readonly _notificationService: NotificationService,
    private readonly _articleLabelsErrorResolverService: ArticleLabelsErrorResolverService,
  ) {
    super(cdr);
  }

  public typeLocked = false;
  public variantLocked = false;

  public get articleVariants() {
    if (!this.article) {
      return [];
    }
    const variants = [...(this.article.articleVariants || [])];
    const def = this.article.defaultArticleVariant;
    if (def && !variants.some(v => v.id === def.id)) {
      variants.unshift(def);
    }
    return variants;
  }

  public showModal(object: IArticle, options?: { type?: ArticleLabelTypes; variantId?: number }): void {
    this.isLoading = false;
    this.article = object;
    this.typeLocked = !!options?.type;
    this.variantLocked = !!options?.variantId;
    this.selectedArticleLabelType = options?.type ?? null;
    const variantId = options?.variantId != null ? Number(options.variantId) : null;
    this.selectedArticleVariantIds = variantId != null
      ? [variantId]
      : (this.articleVariants.length === 1 ? [this.articleVariants[0].id] : []);

    this._articleLabelsService.setBaseUrl(this.article.id);
    super.showModal(object);
    this.cdr.detectChanges();
  }

  public confirmSelectedArticleLabelType(): void {
    if (this.selectedArticleLabelType === this.ownBrandSmallRoundType) {
      const variantId = this.selectedArticleVariantIds[0];
      if (!variantId) {
        return;
      }
      this.closeModal();
      this.openOwnBrand.emit({ variantId, labelType: LabelTypesEnum.SmallRound });
      return;
    }

    if (isInstantArticleLabelType(this.selectedArticleLabelType)) {
      this.isLoading = true;
      this.cdr.detectChanges();

      forkJoin(this.selectedArticleVariantIds.map(id =>
        this._articleLabelsService.createInstant(
          this.article.id,
          id,
          this.selectedArticleLabelType as ArticleLabelTypes,
        ).pipe(
          catchError(error => {
            this._notificationService.pushError(
              'Fehler',
              this._articleLabelsErrorResolverService.getMessage(error.InternalCode)
            );
            return of(null);
          })
        )
      )).pipe(
        finalize(() => {
          this.isLoading = false;
          this.cdr.detectChanges();
        })
      ).subscribe(() => {
        this._notificationService.pushSuccess('Erfolg', 'Etikett wurde erstellt.');
        this.refresh.emit();
        this.closeModal();
      });
      return;
    }

    this.addEditRoundLabelModalComponent.labelType = this.selectedArticleLabelType as ArticleLabelTypes;
    this.addEditRoundLabelModalComponent.article = this.article;
    this.addEditRoundLabelModalComponent.articleVariantIds = this.selectedArticleVariantIds;
    this.addEditRoundLabelModalComponent.showModal(null);
  }
}
